import { useCallback, useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { pickBy } from 'lodash-es'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { DataItem } from 'scrivito'

import { getDataItemAttributesDefaultValues } from '../read/dataItem/getDataItemDefaultValues'
import { getSchemaAttributes } from '../read/schema'
import { buildValidationSchema } from '../validation/buildValidationSchema'
import { errorToast } from '@/Components/Toast'
import { getSchemaName } from '@/utils/dataClassMap'

export const useDataItemEdit = (dataItem: DataItem) => {
  const { t } = useTranslation()
  const dataClassName = dataItem.dataClassName()!
  const schemaName = getSchemaName(dataClassName)
  const { schemaGroups, groupsOrder, schemaAttributes } =
    getSchemaAttributes(schemaName)
  const defaultValues = getDataItemAttributesDefaultValues(dataItem)

  // Build validation schema for visible mandatory attributes
  const validationSchemaObject = buildValidationSchema(
    schemaGroups,
    groupsOrder ?? [],
    schemaAttributes ?? {},
    t,
  )

  const methods = useForm({
    values: defaultValues,
    resolver: yupResolver(validationSchemaObject),
  })
  const { handleSubmit } = methods
  const [isLoading, setIsLoading] = useState(false)

  const onSubmit = async (data: Record<string, unknown>) => {
    setIsLoading(true)
    const definedData = pickBy(data, (e) => e !== undefined)

    try {
      await dataItem.update(definedData)
      return true
    } catch (e) {
      errorToast(e as Error)
      return false
    } finally {
      setIsLoading(false)
    }
  }

  const onFormSubmit = handleSubmit(onSubmit)

  const handleSave = useCallback(() => {
    onFormSubmit()
  }, [onFormSubmit])

  return { handleSave, isLoading, methods, onSubmit, onFormSubmit }
}
